import { makeAutoObservable } from "mobx";
import * as AWS from "aws-sdk";
import SearchState, { API_BASE_URL, DISABLE_CACHEING } from "../../SearchState";
import PopupWindow from "../PopupWindow/PopupWindow";
import { getMusicBucketPath } from "@shared/s3Utils";
import MusicTrackState from "../MusicTrack/MusicTrackState";
import { allTags } from "@shared/constants";
import retryFetch from "../../utils/retryFetch";

/** @typedef {import("@shared/authEndpointDatatypes").S3AuthGetResponse} S3AuthGetResponse */
/** @typedef {import("@shared/musicEndpointTypes").RenameTrackPost} RenameTrackPost */
/** @typedef {import("@shared/musicEndpointTypes").RenameTrackPostResponse} RenameTrackPostResponse */
/** @typedef {import("@shared/musicEndpointTypes").SetEnabledPost} SetEnabledPost */
/** @typedef {import("@shared/musicEndpointTypes").SetLicenseDetailsPost} SetLicenseDetailsPost */
/** @typedef {import("@shared/musicEndpointTypes").SetSiblingGroupPost} SetSiblingGroupPost */
/** @typedef {import("@shared/musicEndpointTypes").SetSiblingGroupPostResponse} SetSiblingGroupPostResponse */
/** @typedef {import("@shared/musicEndpointTypes").SetTagWeightPost} SetTagWeightPost */
/** @typedef {import("@shared/musicEndpointTypes").SetTagWeightPostResponse} SetTagWeightPostResponse */
/** @typedef {import("@shared/musicEndpointTypes").TrackDelete} TrackDelete */
/** @typedef {import("@shared/musicEndpointTypes").TrackDeleteResponse} TrackDeleteResponse */
/** @typedef {import("@shared/typedefs").Tag} Tag */
/** @typedef {import("@shared/typedefs").License} License */

export default class AdminTrackEditorState {
  /** @type {SearchState} */
  searchState;

  /** @type {PopupWindow | null} */
  popupWindow;

  /** @type {MusicTrackState | null} */
  copyTagsBuffer = null;

  /** @type {string} */
  renameTo = "";

  /** @type {string} */
  siblingGroupTo = "";

  /** @type {boolean} */
  deleting = false;

  /**
   * @param {SearchState} searchState
   */
  constructor(searchState) {
    makeAutoObservable(this, {}, { enforceActions: "observed" });
    this.searchState = searchState;
  }

  close = () => {
    this.searchState.hideAdminTrackEditor();
  };

  /**
   * @action
   * @param {string} renameTo
   */
  setRenameTo = (renameTo) => {
    this.renameTo = renameTo;
  };

  /**
   * @action
   * @param {string} siblingGroupTo
   */
  setSiblingGroupTo = (siblingGroupTo) => {
    this.siblingGroupTo = siblingGroupTo;
  };

  /**
   * Copies tags from the currently selected track to the buffer
   * @action
   */
  copyTags = () => {
    this.copyTagsBuffer = this.searchState.selectedTrack;
  };

  /**
   * Pastes tags from the buffer to the currently selected track
   * @action
   */
  pasteTags = () => {
    if (this.searchState.selectedTrack != null && this.copyTagsBuffer != null) {
      this.searchState.selectedTrack.copyTagsFrom(this.copyTagsBuffer.music);
      this.pushAllTags(this.searchState.selectedTrack);
    }
  };

  getParentWithTags = () => {
    if (this.searchState.selectedTrack != null) {
      const selected = this.searchState.selectedTrack;
      if (selected.children != null) {
        /** @type {Array<MusicTrackState>} */ const children =
          this.searchState.selectedTrack.children;
        for (let i = 0; i < children.length; i += 1) {
          /** @type {MusicTrackState} */ const child = children[i];
          if (child.hasTags()) {
            return child;
          }
        }
      }

      for (let i = 0; i < this.searchState.music.length; i += 1) {
        /** @type {MusicTrackState} */ const music = this.searchState.music[i];
        if (
          music !== selected &&
          music.children != null &&
          music.children.length > 0
        ) {
          for (let j = 0; j < music.children.length; j += 1) {
            const child = music.children[j];
            if (child.music.name === selected.music.name) return music;
          }
        }
      }
    }

    return null;
  };

  pasteTagsFromParent = () => {
    /** @type {MusicTrackState} */ const parent = this.getParentWithTags();
    if (this.searchState.selectedTrack != null && parent != null) {
      this.searchState.selectedTrack.copyTagsFrom(parent.music);
      this.pushAllTags(this.searchState.selectedTrack);
    }
  };

  clearTags = () => {
    if (this.searchState.selectedTrack != null) {
      this.searchState.selectedTrack.clearTags();
      this.pushAllTags(this.searchState.selectedTrack);
      console.log(this.taggingButton);
      this.taggingButton.blur();
    }
  };

  commitRename = () => {
    if (this.searchState.selectedTrack != null) {
      const selectedTrack = this.searchState.selectedTrack;
      console.log(`renaming track to ${this.renameTo}`);

      /** @type {RenameTrackPost} */ const renameTrackPost = {
        id: selectedTrack.music.id,
        name: this.renameTo,
      };

      retryFetch(`${API_BASE_URL}/api/v1/rename_track`, {
        method: "POST",
        headers: new Headers({ "content-type": "application/json" }),
        body: JSON.stringify(renameTrackPost),
        mode: "cors",
        withCredentials: true,
        credentials: "include",
      })
        .then((r) => r.json())
        .then((response) => {
          if (response.success) {
            selectedTrack.music.name = this.renameTo;
          }
        });
    }
  };

  commitSiblingGroup = () => {
    if (this.searchState.selectedTrack != null) {
      const selectedTrack = this.searchState.selectedTrack;

      /** @type {SetSiblingGroupPost} */ const setSiblingGroupPost = {
        id: selectedTrack.music.id,
        newSiblingGroup: this.siblingGroupTo,
        oldSiblingGroup: selectedTrack.music.version_search_term,
      };

      retryFetch(`${API_BASE_URL}/api/v1/set_sibling_group`, {
        method: "POST",
        headers: new Headers({ "content-type": "application/json" }),
        body: JSON.stringify(setSiblingGroupPost),
        mode: "cors",
        withCredentials: true,
        credentials: "include",
      })
        .then((r) => r.json())
        .then((response) => {
          if (response.success) {
            selectedTrack.music.version_search_term = this.renameTo;
          }
        });
    }
  };

  /**
   * @action
   * @param {MusicTrackState} track
   * @param {Tag} tag
   * @param {number} value
   */
  toggleTagValue = (track, tag, value) => {
    const tagValue = track.music[tag.urlsafe_name];
    let newValue;
    if (tagValue === value) {
      newValue = -1;
    } else {
      newValue = value;
    }
    track.music[tag.urlsafe_name] = newValue;

    /** @type {SetTagWeightPost} */ const setTagWeightPost = {
      trackId: track.music.id,
      tags: [
        {
          tagUrlSafeName: tag.urlsafe_name,
          value: newValue,
        },
      ],
    };

    retryFetch(`${API_BASE_URL}/api/v1/set_tag_weight`, {
      method: "POST",
      headers: new Headers({ "content-type": "application/json" }),
      body: JSON.stringify(setTagWeightPost),
      mode: "cors",
      withCredentials: true,
      credentials: "include",
    })
      .then((r) => r.json())
      .then((response) => {
        if (response.success) {
          if (response.quality_score) {
            track.music.quality_score = response.quality_score;
          }
        }
      });
  };

  pushAllTags = (track) => {
    /** @type {Array<{ tagUrlSafeName: string, value: number }>} */
    const tags = [];
    for (let i = 0; i < allTags.length; i += 1) {
      /** @type {Tag} */
      const tag = allTags[i];
      tags.push({
        tagUrlSafeName: tag.urlsafe_name,
        value:
          track.music[tag.urlsafe_name] != null
            ? track.music[tag.urlsafe_name]
            : 0,
      });
    }

    /** @type {SetTagWeightPost} */ const setTagWeightPost = {
      trackId: track.music.id,
      tags,
    };
    console.log(setTagWeightPost);

    retryFetch(`${API_BASE_URL}/api/v1/set_tag_weight`, {
      method: "POST",
      headers: new Headers({ "content-type": "application/json" }),
      body: JSON.stringify(setTagWeightPost),
      mode: "cors",
      withCredentials: true,
      credentials: "include",
    })
      .then((r) => r.json())
      .then((response) => {
        if (response.success) {
          console.log("set successful");
        }
      });
  };

  focus = () => {
    if (this.popupWindow != null) {
      if (this.popupWindow.window != null) {
        this.popupWindow.window.focus();
      }
    }
  };

  /**
   * @action
   * @param {PopupWindow | null} popupWindow
   */
  setPopupWindow = (popupWindow) => {
    this.popupWindow = popupWindow;
    if (this.popupWindow != null) {
      this.popupWindow.window.addEventListener(
        "keydown",
        this.searchState.keyListener,
      );
    }
  };

  deleteSelectedTrack = () => {
    if (this.searchState.selectedTrack != null) {
      const selectedTrack = this.searchState.selectedTrack;
      this.deleting = true;
      retryFetch(`${API_BASE_URL}/api/v1/auth/s3_credentials`, {
        method: "GET",
        redirect: "error",
        withCredentials: true,
        credentials: "include",
        cache: DISABLE_CACHEING ? "reload" : "default",
      })
        .then((r) => r.json())
        .then((r) => {
          if (!r.authorized) {
            window.alert("You are not authorized to delete");
            return;
          }

          let deleted = 0;
          const onFinishDeleting = () => {
            console.log("Successfully deleted mp3 and wav tracks");

            /** @type {TrackDelete} */
            const trackDelete = {
              id: selectedTrack.music.id,
              oldSiblingGroup: selectedTrack.music.version_search_term,
            };

            retryFetch(`${API_BASE_URL}/api/v1/track`, {
              method: "DELETE",
              headers: new Headers({ "content-type": "application/json" }),
              body: JSON.stringify(trackDelete),
              mode: "cors",
              withCredentials: true,
              credentials: "include",
            })
              .then((r) => r.json())
              .then((r) => {
                if (r.success) {
                  this.searchState.deleteTrack(selectedTrack);
                  this.deleting = false;
                }
              });
          };

          // Set up S3

          AWS.config.update({
            region: r.bucketRegion,
            credentials: new AWS.CognitoIdentityCredentials({
              IdentityPoolId: r.identityPoolId,
            }),
          });

          const s3 = new AWS.S3({
            apiVersion: "2006-03-01",
            params: { Bucket: r.bucketName },
          });

          // Delete the mp3

          s3.deleteObject(
            {
              Key: getMusicBucketPath(selectedTrack.music, "mp3"),
            },
            (err) => {
              if (err) {
                console.error(err);
              }
              deleted += 1;
              if (deleted >= 2) {
                onFinishDeleting();
              }
            },
          );

          // Delete the wav

          s3.deleteObject(
            {
              Key: getMusicBucketPath(selectedTrack.music, "wav"),
            },
            (err) => {
              if (err) {
                console.error(err);
              }
              deleted += 1;
              if (deleted >= 2) {
                onFinishDeleting();
              }
            },
          );
        });
    }
  };

  setEnabled = (track, enabled) => {
    /** @type {SetEnabledPost} */ const setEnabledPost = {
      trackId: track.music.id,
      enabled,
    };

    retryFetch(`${API_BASE_URL}/api/v1/set_enabled`, {
      method: "POST",
      headers: new Headers({ "content-type": "application/json" }),
      body: JSON.stringify(setEnabledPost),
      mode: "cors",
      withCredentials: true,
      credentials: "include",
    }).then(() => {
      track.music.is_available_to_license = enabled;
    });
  };

  /** @type {NodeJS.Timeout | null} */
  pushLicenseTimeout = null;

  pushLicenseDetails = (track) => {
    if (this.pushLicenseTimeout != null) clearTimeout(this.pushLicenseTimeout);
    this.pushLicenseTimeout = setTimeout(() => {
      /** @type {SetLicenseDetailsPost} */ const setEnabledPost = {
        trackId: track.music.id,
        licenses: track.licenses,
      };

      retryFetch(`${API_BASE_URL}/api/v1/license_details`, {
        method: "POST",
        headers: new Headers({ "content-type": "application/json" }),
        body: JSON.stringify(setEnabledPost),
        mode: "cors",
        withCredentials: true,
        credentials: "include",
      }).then(() => {
        console.log("pushed license details");
      });
    }, 2000);
  };

  getLicenses = () => {
    if (this.searchState.selectedTrack != null) {
      const selected = this.searchState.selectedTrack;
      return selected.licenses;
    }
    return [];
  };

  addLicense = () => {
    if (this.searchState.selectedTrack != null) {
      /** @type {License} */
      const newLicense = {
        expiration: new Date().toISOString(),
        exclusive: false,
        notes: "",
      };
      this.searchState.selectedTrack.licenses.push(newLicense);
    }
  };

  /**
   * @action
   * @param {number} index
   */
  deleteLicense = (index) => {
    if (this.searchState.selectedTrack != null) {
      const selectedTrack = this.searchState.selectedTrack;
      selectedTrack.licenses.splice(index, 1);
      this.pushLicenseDetails(selectedTrack);
    }
  };

  /**
   * @action
   * @param {number} index
   * @param {string} note
   */
  setLicenseNote = (index, note) => {
    if (this.searchState.selectedTrack != null) {
      this.searchState.selectedTrack.licenses[index].notes = note;
      this.pushLicenseDetails(this.searchState.selectedTrack);
    }
  };

  /**
   * @action
   * @param {number} index
   * @param {string} expiration
   */
  setLicenseExpiration = (index, expiration) => {
    if (this.searchState.selectedTrack != null) {
      this.searchState.selectedTrack.licenses[index].expiration = expiration;
      this.pushLicenseDetails(this.searchState.selectedTrack);
    }
  };

  /**
   * @action
   * @param {number} index
   * @param {boolean} exclusive
   */
  setLicenseExclusive = (index, exclusive) => {
    if (this.searchState.selectedTrack != null) {
      this.searchState.selectedTrack.licenses[index].exclusive = exclusive;
      this.pushLicenseDetails(this.searchState.selectedTrack);
    }
  };
}
